import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { Subject } from 'rxjs';
import { ConfigService } from './core/services/config.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { NgxUiLoaderService, NgxUiLoaderModule, NgxUiLoaderConfig } from 'ngx-ui-loader';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AuthService } from './core/services/auth.service';
import { PermissionService } from './core/services/permission.service';
import { Permission } from './core/enums/permission.enum';
import { FeedbackHostComponent } from './modules/feedback/components/feedback-host.component';
import { AiAssistantHostComponent } from './modules/ai-assistant/components/ai-assistant-host.component';
import { AssistanceLauncherComponent } from './shared/components/assistance-launcher/assistance-launcher.component';
const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsColor: '#0072C6',
  fgsColor: '#0072C6',
  fgsType: 'ball-spin',
  bgsOpacity: 0.5,
  blur: 5,
};

@Component({
  selector: 'em-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    ToastModule,
    NgxUiLoaderModule,
    ConfirmDialogModule,
    FeedbackHostComponent,
    AiAssistantHostComponent,
    AssistanceLauncherComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [MessageService],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthService,
    private configService: ConfigService,
    private messageService: MessageService,
    private ngxService: NgxUiLoaderService,
    private permissionService: PermissionService,
    private translate: TranslateService,
  ) {}

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  ngOnInit(): void {
    if (!this.authService.isLoggedIn()) {
      return;
    }

    this.ngxService.start();

    this.configService.getAppType().subscribe({
      next: (appType) => {
        this.ngxService.stop();
        const shouldRedirect = this.shouldRunStartupRedirect();
        if (shouldRedirect) {
          this.redirectBasedOnAppType(appType);
        }
      },
      error: (error) => {
        this.ngxService.stop();
        if (error?.status !== 401) {
          console.error('Error fetching app type: ', error);
          this.showError(this.translate.instant('shared.errors.fetchAppType'));
        }
      },
    });
  }

  private shouldRunStartupRedirect(): boolean {
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    return currentUrl === '/' || currentUrl === '';
  }

  private redirectBasedOnAppType(appType: string | null) {
  const hasDashboardAccess =
    this.permissionService.hasPermission(Permission.DASHBOARD_GLOBAL) ||
    this.permissionService.hasPermission(Permission.DASHBOARD_CLIENT) ||
    this.permissionService.hasPermission(Permission.DASHBOARD_LEADER) ||
    this.permissionService.hasPermission(Permission.DASHBOARD_LANGUAGE) ||
    this.permissionService.hasPermission(Permission.DASHBOARD_PROMOTION);    const hasCardsViewRead = this.permissionService.hasPermission(Permission.CARDS_VIEW_READ);

    if (appType === 'staffing') {
      if (hasDashboardAccess) {
        this.router.navigate(['/staffing/dashboard']);
      } else {
        this.router.navigate(['/staffing/client']);
      }
    } else if (appType === 'hr') {
      if (hasCardsViewRead) {
        this.router.navigate(['/employees/card-view']);
      } else if (hasDashboardAccess) {
        this.router.navigate(['/staffing/dashboard']);
      }
    }
  }

  private showError(message: string): void {
    this.messageService.add({ severity: 'error', summary: this.translate.instant('common.toast.error'), detail: message + '.' });
  }
}
